import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  clientExpiryMilliseconds,
  redirectionTimeoutDuration,
  routesWithClientId,
  routes,
} from 'utils/constants';
import { redirectBackToClientHelper } from 'utils/helpers';
import { useRouter } from 'next/router';
import {
  LocalStorageKeys,
  useLocalStorage,
  setLocalStorage,
} from 'utils/localStorage';
import { DatadogClient } from '@workivate/tho-web-shared';

interface SvgIcons {
  [key: string]: SVGSVGElement['outerHTML'] | undefined | -1;
}

export type SessionContextType = {
  clientId?: string;
  redirectBackToClient: (error?: string) => void;
  username?: string; // needed by MFA Setup
  setUsername: (username?: string) => void; // set by Login
  showGenericError: boolean;
  setShowGenericError: (show: boolean) => void;
  svgIcons: SvgIcons;
  setSvgIcons: React.Dispatch<React.SetStateAction<SvgIcons>>;
  isLoginPersistent: boolean; // whether the user selected "keep me logged in" on the login page
  setIsLoginPersistent: (isLoginPersistent: boolean) => void;
};

export const SessionContext = createContext<SessionContextType>({
  clientId: undefined,
  // setting redirectBackToClientHelper here allows testing of the expiry logic
  redirectBackToClient: (error) => redirectBackToClientHelper({ error }),
  username: undefined,
  setUsername: () => {},
  showGenericError: false,
  setShowGenericError: () => {},
  svgIcons: {},
  setSvgIcons: () => ({}),
  isLoginPersistent: false,
  setIsLoginPersistent: () => {},
});

export const useSessionContext = () => useContext(SessionContext);

type SessionProviderProps = {
  children: React.ReactNode;
};

export const SessionProvider = ({ children }: SessionProviderProps) => {
  const [username, setUsername] = useState<string | undefined>(undefined);
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const [isClientIdExpired, setIsClientIdExpired] = useState<boolean>(false);
  const [isLoginPersistent, setIsLoginPersistent] = useState<boolean>(false);
  const [showGenericError, setShowGenericError] = useState(false);
  const [svgIcons, setSvgIcons] = useState<SvgIcons>({});

  // if the user refreshes the page, we need to keep the original expiry time
  // otherwise the user could be stuck because the clientId has expired
  // but USL thinks it's still valid
  // so we use localStorage and check this at component mount
  const [storedClientId, setStoredClientId] = useLocalStorage<string>(
    LocalStorageKeys.clientId
  );
  const [storedClientIdExpiryDate, setStoredClientIdExpiryDate] =
    useLocalStorage<number>(LocalStorageKeys.clientIdExpiryDate);

  const router = useRouter();

  const checkClientIdExpiry = (expiryTime: number) => {
    const timeRemaining = expiryTime - Date.now();

    if (timeRemaining <= 0) {
      setIsClientIdExpired(true);
    }

    if (timeRemaining <= -1 * redirectionTimeoutDuration) {
      // Show a help page in case the redirect doesn't work
      // Native apps sometimes don't do the redirect
      // or require user interaction with a popup that can be missed
      // delay is so that if the redirect works, the user doesn't see the help page
      // Only redirect if we're NOT already on the session-expired page to avoid endless refresh
      if (router?.pathname !== routes['/session-expired']) {
        router.replace({
          pathname: routes['/session-expired'],
          query: router.query,
        });
      }
    }
  };

  useEffect(() => {
    // start checking the expiry of the client ID
    // we use setInterval insted of setTimeout in case the app is "backgrounded" or otherwise frozen
    // this can happen on Natives and a timeout can be missed
    // when the user returns to the app, the interval should catch expired client ID

    // Don't run the interval if we're already on the session-expired page
    if (router?.pathname === routes['/session-expired']) {
      return;
    }

    if (!clientId) return;

    let expiryTime = Date.now() + clientExpiryMilliseconds;

    // check if there is already an expiry date for the current clientId
    if (storedClientIdExpiryDate && storedClientId === clientId) {
      expiryTime = storedClientIdExpiryDate;
      const timeRemaining = storedClientIdExpiryDate - Date.now();

      if (timeRemaining <= 0) {
        setIsClientIdExpired(true); // client ID has expired
      }
    } else {
      // save in localStorage in case the page is refreshed
      setStoredClientId(clientId);
      setStoredClientIdExpiryDate(expiryTime);
    }

    const interval = setInterval(() => {
      checkClientIdExpiry(expiryTime);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [clientId, router.pathname]);

  useEffect(() => {
    if (!router.isReady) return;
    const clientIdParam = router.query['client_id'] as string;
    if (clientId !== clientIdParam) {
      setClientId(clientIdParam);
      DatadogClient.setGlobalContextProperty('public_client_id', clientIdParam);
    }

    const redirectToParam = router.query['redirectTo'];
    if (redirectToParam !== undefined && redirectToParam !== '') {
      setLocalStorage(LocalStorageKeys.redirectTo, redirectToParam);
    }
  }, [router.isReady]);

  useEffect(() => {
    const currentRoute = router.pathname;

    if (
      (routesWithClientId as string[]).includes(currentRoute) &&
      isClientIdExpired
    ) {
      redirectBackToClientHelper({ clientId, router });
    }
  }, [isClientIdExpired, router.pathname]);

  // memoise to prevent the object being recreated on every render
  const sessionContext = useMemo(
    () => ({
      clientId,
      redirectBackToClient: (error?: string) =>
        redirectBackToClientHelper({ clientId, error, router }),
      username,
      setUsername,
      showGenericError,
      setShowGenericError,
      svgIcons,
      setSvgIcons,
      isLoginPersistent,
      setIsLoginPersistent,
    }),
    [clientId, isLoginPersistent, username, showGenericError, svgIcons]
  );

  return (
    <SessionContext.Provider value={sessionContext}>
      {children}
    </SessionContext.Provider>
  );
};
